import { type Progress, ThreeStepProgress } from '@/components/charts/threeStepProgress'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/common/accordion'
import { Button } from '@/components/common/button'
import { Markdown } from '@/components/common/markdown'
import { ProtocolImage } from '@/components/common/protocolImage'
import { SeverityIcon } from '@/components/common/severityIcon'
import { TokenImage } from '@/components/common/tokenImage'
import { ArrowIcon } from '@/components/icons/arrow'
import { ExternalLinkIcon } from '@/components/icons/externalLink'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { getExplorerLink } from '@/utils/explorer'
import { getScoreRange, getScoreValue } from '@/utils/getScoreRangeValue'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getSeverityTextClass } from '@/utils/scoreColors'
import { formatAmount } from '@vaultsfyi/common/utils'
import type { ComponentProps } from 'react'
import { useState } from 'react'

interface Props {
  vault: Vault
  score: NonNullable<Vault['score']>
}

export const ReputationBreakdown = ({ vault, score }: Props) => {
  const { protocol, network, asset, holdersData } = vault
  const protocolDisplayName = getProtocolDisplayName(protocol)
  const { protocolTvlScore, assetScore, holderScore, vaultTvlScore } = score

  const protocolRangeValue = getScoreRange(protocolTvlScore)
  const assetRangeValue = getScoreRange(assetScore)
  const tvlRangeValue = getScoreRange(vaultTvlScore)
  const holderRangeValue = getScoreRange(holderScore)

  const explorerLink = getExplorerLink(network.name)

  return (
    <>
      <ul className="mx-auto flex flex-col gap-2">
        <ListItem label="Protocol:" score={protocolRangeValue}>
          {protocol.protocolUrl ? (
            <Button
              variant="default"
              size="default"
              className="inline-flex max-w-full items-center justify-start gap-2 rounded-full underline"
              title={`${protocolDisplayName}${protocol.version ? ` (${protocol.version})` : ''}`}
              asChild
            >
              <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
                <ProtocolDisplay protocol={protocol} />
              </a>
            </Button>
          ) : (
            <div
              className="inline-flex max-w-full items-center gap-2"
              title={`${protocolDisplayName}${protocol.version ? ` (${protocol.version})` : ''}`}
            >
              <ProtocolDisplay protocol={protocol} />
            </div>
          )}
        </ListItem>
        <ListItem label="Token:" score={assetRangeValue}>
          {explorerLink ? (
            <Button
              variant="default"
              size="default"
              className="inline-flex max-w-full items-center justify-start gap-2 rounded-full underline"
              asChild
            >
              <a href={`${explorerLink}/address/${asset.address}`} target="_blank" rel="noreferrer">
                <TokenImage symbol={asset.symbol} className="size-6" />
                <span className="max-w-full truncate">{asset.symbol}</span>
                <ExternalLinkIcon size={16} className="shrink-0" />
              </a>
            </Button>
          ) : (
            <div className="inline-flex max-w-full items-center gap-2">
              <TokenImage symbol={asset.symbol} className="size-6" />
              <span className="max-w-full truncate">{asset.symbol}</span>
            </div>
          )}
        </ListItem>
        <ListItem label="Vault history:" score={tvlRangeValue}>
          <span>
            <VaultHistoryLabel score={tvlRangeValue} />
          </span>
        </ListItem>
        <ListItem label="# of holders:" score={holderRangeValue}>
          <span className="max-w-full truncate">
            {holdersData.totalCount ? formatAmount(holdersData.totalCount) : '-'}
          </span>
        </ListItem>
        {score.totalScorePenalty > 0 && <PenaltyImpact score={score} />}
      </ul>
    </>
  )
}

const ProtocolDisplay = ({ protocol }: { protocol: Vault['protocol'] }) => {
  const protocolDisplayName = getProtocolDisplayName(protocol)

  return (
    <>
      <ProtocolImage protocol={protocol} className="size-6" />
      <span className="max-w-full truncate">
        <span>{protocolDisplayName}</span>
        {protocol.version ? ` (${protocol.version})` : ''}
      </span>
    </>
  )
}

const VaultHistoryLabel = ({ score }: { score: Progress }) => {
  switch (score) {
    case 0:
    case 1:
      return <p>Low</p>
    case 2:
      return <p>Medium</p>
    case 3:
      return <p>High</p>
  }
}
interface ListItemProps {
  label: string
  score: Progress | undefined
}

const PenaltyImpact = ({ score }: { score: NonNullable<Vault['score']> }) => {
  const { totalScorePenalty, penaltyComponents } = score
  const [open, setOpen] = useState('')

  const worstSeverity = penaltyComponents.some((p) => 'severity' in p && p.severity === 'critical')
    ? 'critical'
    : penaltyComponents.some((p) => 'severity' in p && p.severity === 'warning')
      ? 'warning'
      : 'info'

  return (
    <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
      <AccordionItem value="penalties">
        <li className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <p className="w-full xs:w-26">Penalty impact:</p>
          <div className="flex items-center gap-5">
            <span className={cn('font-medium', getSeverityTextClass(worstSeverity))}>
              -{getScoreValue(totalScorePenalty)}
            </span>
            <AccordionTrigger className="flex items-center gap-1 text-accent">
              {open ? 'Hide details' : 'Show details'}
              <ArrowIcon direction="down" size={20} className="transition-transform [[data-state=open]_&]:rotate-180" />
            </AccordionTrigger>
          </div>
        </li>
        <AccordionContent>
          <ul className="flex flex-col gap-2 pt-2">
            {penaltyComponents.map((penalty, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <div
                  className={cn(
                    'flex items-center gap-1',
                    'severity' in penalty ? getSeverityTextClass(penalty.severity) : 'text-disabled'
                  )}
                >
                  <span className="whitespace-nowrap font-medium">-{getScoreValue(penalty.value)}</span>
                  {'severity' in penalty && <SeverityIcon severity={penalty.severity} className="shrink-0" />}
                </div>
                <div className="min-w-0 flex-1">
                  {'content' in penalty && penalty.content && (
                    <Markdown className="break-words">{penalty.content}</Markdown>
                  )}
                  {'resolution' in penalty && penalty.resolution && (
                    <Markdown className="mt-1 break-words">{penalty.resolution}</Markdown>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

const ListItem = ({ label, score, children }: ListItemProps & ComponentProps<'li'>) => {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <p className="w-full xs:w-26 font-medium">{label}</p>
      <div className="flex min-w-0 flex-1 flex-grow font-medium">{children}</div>
      <ThreeStepProgress progress={score} className="w-fit" />
    </li>
  )
}
